import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  ArrowRight,
  Users,
  Sparkles,
  User,
  Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Seo } from '@/components/seo/Seo';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ContactDialog } from '@/components/contact/ContactDialog';
import { cn } from '@/lib/utils';
import { useAuth } from '@/components/auth/AuthContext';

type PlanId = 'daily' | 'per_lens' | 'all_lenses';

type Tab = 'individual' | 'team' | 'expert';

const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'individual', label: 'Individual', icon: <User className="w-4 h-4" /> },
  { id: 'team', label: 'Business', icon: <Users className="w-4 h-4" /> },
  { id: 'expert', label: 'Expert Custom Lens', icon: <Sparkles className="w-4 h-4" /> },
];

const Pricing = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('individual');

  return (
    <>
      <Seo
        title="Pricing: Applyt"
        description="Simple, transparent pricing. Choose the plan that fits your needs."
      />
      <div className="min-h-screen bg-background text-foreground font-sans">
        <Header />

        {/* Hero */}
        <section className="relative z-10 pt-20 pb-8 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Pricing
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Choose the plan that fits your workflow.
            </p>
          </div>
        </section>

        {/* Tab switcher */}
        <section className="relative z-10 pb-4 px-6">
          <div className="max-w-xl mx-auto flex justify-center">
            <div className="inline-flex rounded-full border border-border bg-card p-1 gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all',
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="relative z-10 py-12 px-6">
          <div className="max-w-5xl mx-auto">
            {activeTab === 'individual' && <IndividualPlans navigate={navigate} />}
            {activeTab === 'team' && <TeamPlans />}
            {activeTab === 'expert' && <ExpertPlan />}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="relative z-10 pb-16 px-6">
          <p className="text-center text-xs text-muted-foreground max-w-2xl mx-auto">
            Usage limits apply. Prices shown don't include applicable tax. Plans and pricing are subject to change.
          </p>
        </section>

        <Footer />
      </div>
    </>
  );
};

/* ─── Individual ─── */
function IndividualPlans({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  const { user, isAnonymous } = useAuth();

  const handleGetStarted = () => {
    if (!user || isAnonymous) {
      navigate('/beta_access');
      return;
    }
    navigate('/account');
  };

  const plans: Array<{
    id: PlanId;
    name: string;
    price: string;
    description: string;
    features: string[];
    popular: boolean;
    period?: string;
    badge?: string;
    monthlyUsageNote?: string;
  }> = [
      {
        id: 'daily',
        name: 'Run for a Day',
        price: '$7',
        description: 'You receive free credits to create your first deliverable. Then you can unlock more credits.',
        features: [
          'Access to all expert systems',
          'Create strategies, plans, and deliverables',
          'Full skill execution',
          'Export your work',
        ],
        popular: false,
      },
      {
        id: 'per_lens',
        name: 'Starter',
        price: '$29',
        description: 'Choose one lens. Perfect for focused expertise.',
        monthlyUsageNote:
          'Per month with annual subscription discount ($350 billed up front). $39 if billed monthly.',
        features: [
          'Access to one selected expert system',
          'Create strategies, plans, and deliverables',
          'Full skill execution',
          'Export your work',
          'Workflow integration',
          'Basic support',
        ],
        popular: false,
      },
      {
        id: 'all_lenses',
        name: 'Pro',
        price: '$99',
        description: 'Get the most out of Applyt. Best for power users.',
        monthlyUsageNote:
          'Per month with annual subscription discount ($1200 billed up front). $129 if billed monthly.',
        features: [
          'Full access to all expert systems',
          'Unlimited work generation',
          'Advanced skill execution',
          'Switch experts within chat',
          'Workflow integration',
          'Prority support',
        ],
        popular: true,
      },
    ];

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {plans.map((plan, i) => (
        <div
          key={i}
          className={cn(
            'relative rounded-2xl p-8 flex flex-col transition-all duration-300',
            plan.popular
              ? 'bg-primary/[0.06] border-2 border-primary/40 shadow-md md:scale-[1.03]'
              : 'bg-card border border-border/60 hover:border-primary/30 shadow-sm'
          )}
        >
          {plan.popular && (
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
              <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-semibold tracking-wide">
                Most Popular
              </span>
            </div>
          )}

          <div className="mb-6 min-h-[96px]">
            <h3 className="text-lg font-semibold mb-1">{plan.name}</h3>
            <p className="text-sm text-muted-foreground">{plan.description}</p>
          </div>

          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-3xl font-bold">{plan.price}</span>
            <span className="text-muted-foreground text-sm">{plan.period}</span>
          </div>
          {plan.badge && (
            <span className="text-xs font-medium text-primary">{plan.badge}</span>
          )}
          {plan.monthlyUsageNote && (
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {plan.monthlyUsageNote}
            </p>
          )}

          <ul className="mt-6 space-y-3 flex-1">
            {plan.features.map((f, j) => (
              <li key={j} className="flex items-start gap-2.5">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">{f}</span>
              </li>
            ))}
          </ul>

          <Button
            variant={plan.popular ? 'default' : 'outline'}
            className="w-full mt-8"
            onClick={handleGetStarted}
          >
            Get Started
          </Button>
          <p className="mt-3 min-h-[20px] text-center text-xs text-muted-foreground">
            {plan.monthlyUsageNote ? 'No commitment. Cancel anytime.' : ''}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ─── Business ─── */
function TeamPlans() {
  const [contactOpen, setContactOpen] = useState(false);
  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex h-full flex-col rounded-3xl border border-border/60 bg-card p-8 shadow-sm transition-all duration-300 hover:border-primary/30">
        <div className="flex items-start justify-between gap-4">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-border/60 bg-background text-foreground">
            <Building2 className="h-6 w-6 stroke-[1.5]" />
          </div>
        </div>

        <div className="mt-8">
          <h3
            className="text-3xl font-semibold tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Team 5+
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Replace experts, agencies, and internal execution with secure, scalable expert systems in a collaborative workspace built for startups and growing businesses.
          </p>
        </div>

        <div className="mt-8 rounded-2xl border border-border/50 bg-background/70 p-5">
          <p className="text-sm font-semibold text-foreground">
            From $499/month (5 seats included).
          </p>
        </div>

        <div className="my-8 border-t border-border/60" />

        <div className="flex flex-1 flex-col">
          <p className="mb-4 text-sm font-semibold text-foreground">Includes:</p>

          <ul className="space-y-3">
            {[
              'Run expert methods across your team',
              'Full access to all expert systems',
              'Generate strategies, plans, and deliverables at scale',
              'Switch expert methods within your workflow',
              'Standardize how work gets done',
              'Advanced skill execution',
              'Workflow and tools integration',
              'Team collaboration and shared outputs',
              'Central billing and admin control',
              'API access (automation-ready)',
              'Priority support',
            ].map((feature) => (
              <li key={feature} className="flex items-start gap-2.5">
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <span className="text-sm text-muted-foreground">{feature}</span>
              </li>
            ))}
          </ul>

          <Button
            className="mt-8 w-full"
            onClick={() => setContactOpen(true)}
          >
            Contact Sales
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        title="Business subscription inquiry"
        description="Tell us about your team and we'll get back to you with next steps."
        defaultSubject="Business subscription inquiry"
        context="Pricing → Business (Team 5+)"
      />
    </div>
  );
}

/* ─── Expert Custom Lens ─── */
function ExpertPlan() {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto">
      <div className="rounded-2xl border border-border/60 bg-card p-10 shadow-sm text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-6">
          <Sparkles className="w-7 h-7 text-primary" />
        </div>

        <h3
          className="text-2xl font-bold mb-3"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Turn Your Expertise Into a Scalable Revenue Engine
        </h3>

        <p className="text-muted-foreground leading-relaxed mb-6 max-w-lg mx-auto">
          We convert your methodology into an AI-powered system that your clients can run at scale without your time. Your frameworks, your logic, your standards get executed automatically. You keep ownership.
          <br />You earn from client usage.
        </p>
        <p className="text-muted-foreground leading-relaxed mb-6 max-w-lg mx-auto">
          What you get:
        </p>
        <div className="grid grid-cols-1 gap-4 text-left mb-8 max-w-lg mx-auto">
          {[
'Your methodology turned into a working system (not content) ',
'Clients get real outputs: strategies, plans, deliverables ',
'White-label experience under your name ',
'Revenue share on client usage',
'You keep your full intellectual property ownership ',
'Feedback-based continuous improvement and iteration',
'Dashboard and analytics on how clients use your expert system',
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-sm text-muted-foreground">{f}</span>
            </div>
          ))}
        </div>

        <Button
          size="lg"
          onClick={() => navigate('/become_expert')}
        >
          Contact Us
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>

        <p className="text-xs text-muted-foreground mt-4">
          We'll get back to you within 48 hours to discuss your methodology and goals.
        </p>
      </div>
    </div>
  );
}

export default Pricing;
